import { ENDPOINTS } from "../../constant/routes";
import { request } from "../../services/requests";

export type PromoType = "voucher" | "free_delivery";
export type DiscountKind = "amount" | "percent";
export type PromoScope = "all" | "sellers" | "genera";
export type Distribution = "code" | "claim" | "given";
export type GiveTrigger = "cart_reminder_2" | "signup" | "list";
export type Funding = "inflos" | "sellers" | "split";
/** draft | active | paused | ended, as stored */
export type PromoStatus = "draft" | "active" | "paused" | "ended";
/** What the list shows: Scheduled and Live are derived from the dates */
export type PromoState = "draft" | "scheduled" | "live" | "paused" | "ended";

/** The settings the create/edit dialog sends. Money is SGD. */
export interface PromotionInput {
  name: string;
  title: string | null;
  promo_type: PromoType;
  discount_kind: DiscountKind;
  /** S$ off, or the percent for a % off */
  amount: number | null;
  /** The cap for a % off; the delivery cap for free delivery */
  max_discount: number | null;
  min_spend: number;
  scope: PromoScope;
  /** Seller usernames or genus names */
  scope_values: string[];
  first_order_only: boolean;
  distribution: Distribution;
  code: string | null;
  claim_slug: string | null;
  give_trigger: GiveTrigger | null;
  /** Wallet expiry after a give or claim; null = the promotion's end */
  wallet_ttl_hours: number | null;
  uses_per_buyer: number;
  /** null = per lifetime */
  per_buyer_window_days: number | null;
  total_uses: number | null;
  max_claims: number | null;
  budget: number | null;
  funded_by: Funding;
  /** ISO UTC; null = from publishing */
  starts_at: string | null;
  /** ISO UTC; null = no end */
  expires_at: string | null;
}

export interface PromotionStats {
  uses: number;
  /** SGD, from paid orders */
  cost: number;
  /** SGD value of the paid orders it was used on */
  gmv: number;
  /** cost / budget, 0..1+ (null without a budget) */
  budget_used: number | null;
  claims: number;
  given: number;
  in_wallets: number;
}

export interface Promotion extends PromotionInput {
  id: string;
  /** An old Stripe code from before vouchers: read-only */
  legacy: boolean;
  currency: string;
  /** Seller usernames for scope "sellers" (scope_values then holds user ids) */
  scope_labels: string[];
  status: PromoStatus;
  state: PromoState;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  stats: PromotionStats;
}

export interface PromotionDetail extends Promotion {
  stats: PromotionStats & {
    orders: number;
    buyers: number;
    cost_per_order: number | null;
    /** Buyers with a paid order after the one that used it */
    bought_again: number;
    uses_today: number;
    by_month: { month: string; uses: number; cost: number }[];
    recent: { order_id: string; buyer_id: string; buyer: string | null; amount: number; currency: string; at: string | null }[];
  };
  /** Only on PUT */
  changed?: string[];
}

export interface PromotionsResponse {
  /** false until marketplace migration 021: legacy codes only, no writes */
  available: boolean;
  wallet_available: boolean;
  promotions: Promotion[];
  summary: {
    live_now: number;
    uses_30d: number;
    cost_30d: number;
    all_time: { uses: number; cost: number; gmv: number };
    currency: string;
  };
  counts: Record<"all" | "live" | "scheduled" | "draft" | "paused" | "ended", number>;
}

export interface GiveResult {
  given: number;
  already_had: number;
  not_found: string[];
  expires_at: string | null;
}

export type PromotionAction = "publish" | "pause" | "resume" | "end";

const path = (id: string) => `${ENDPOINTS.PROMOTIONS}/${encodeURIComponent(id)}`;

export const listPromotions = (token: string) => request(token).get<PromotionsResponse>(ENDPOINTS.PROMOTIONS);

export const getPromotion = (id: string, token: string) => request(token).get<PromotionDetail>(path(id));

export const createPromotion = (input: PromotionInput, publish: boolean, token: string) =>
  request(token).post<PromotionDetail>(ENDPOINTS.PROMOTIONS, { ...input, publish });

export const updatePromotion = (id: string, input: PromotionInput, publish: boolean, token: string) =>
  request(token).put<PromotionDetail>(path(id), { ...input, publish });

export const changePromotionStatus = (id: string, action: PromotionAction, token: string) =>
  request(token).post<PromotionDetail>(`${path(id)}/${action}`);

export const duplicatePromotion = (id: string, token: string) => request(token).post<PromotionDetail>(`${path(id)}/duplicate`);

export const givePromotion = (id: string, buyers: string[], token: string) => request(token).post<GiveResult>(`${path(id)}/give`, { buyers });
